// Companion/blocks/StudySetFooter.tsx
//
// The footer of a study set: how far through it the student is, and the one way
// into the full session. What the set *is* is the block's header, which is the
// same `scopeLabel`, so the footer does not say it twice.
//
// One row, one action. A second way in — the "Start a full session" link that
// built a bigger set from the same filter — read as a rival to the button next
// to it and was dropped (Kevin, 2026-09-17); the set the turn served is the set
// the footer opens.
//
// It is drawn for every set — a lazy set has nothing else to show, and a
// restored read-only block still tells the student what they did — so the
// footer, not the items, is what makes a set a set.

import { useState, type RefObject } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import {
  launchStudySet,
  type StudySetLaunchRefusal,
} from '@/services/studySets';
import type {
  ChatStudySetBlockPayload,
  ChatStudySetProgress,
} from '@/types/chatBlocks';
import type { ChatNavigationTarget } from '@/types/chatStream';

/** The copy a served question already uses when the daily allowance is spent. */
export const QBANK_LIMIT_REACHED_COPY =
  'You have used your free QBank questions for today.';

const LAUNCH_FAILED_COPY = 'That session could not be opened. Try again.';

/** A lazy set whose filter draws nothing by the time the student opens it. */
export const LAUNCH_UNAVAILABLE_COPY = 'Nothing left to study in this set.';

const REFUSAL_COPY: Record<StudySetLaunchRefusal, string> = {
  limit_reached: QBANK_LIMIT_REACHED_COPY,
  launch_unavailable: LAUNCH_UNAVAILABLE_COPY,
  unavailable: LAUNCH_FAILED_COPY,
};

/**
 * What the one button says, or null when the set has no full session worth
 * opening. A single question is the whole set — "Open full session" on it would
 * open the same question the student is already looking at (design 4.2).
 *
 * A finished set offers the review only when the server says there is one: a
 * question set answered entirely in the thread never had a block, so there are
 * no explanations to open and "Review" would lead nowhere. The server says so
 * by pointing the launch target at the review route, which it does only once a
 * launched block exists. Such a set says "Done" instead, which folds it away
 * (ORA-833) — it was a disabled label for the state until then.
 */
export const studySetButtonLabel = (
  payload: ChatStudySetBlockPayload,
  progress: ChatStudySetProgress
): string | null => {
  if (payload.requestedCount <= 1) return null;

  if (payload.status === 'completed' || progress.done >= progress.total) {
    return payload.launch?.route === 'study.qbank.review' ? 'Review' : 'Done';
  }

  return payload.status === 'launched' ? 'Resume' : 'Open Full Session';
};

/** "1 of 3 done", with the score once a question set has one. */
export const studySetProgressText = (
  progress: ChatStudySetProgress
): string => {
  const base = `${progress.done} of ${progress.total} done`;

  return progress.correct !== undefined && progress.done > 0
    ? `${base} · ${progress.correct} correct`
    : base;
};

export interface StudySetFooterProps {
  payload: ChatStudySetBlockPayload;
  /** The live progress, which is the payload's plus anything done in-thread. */
  progress: ChatStudySetProgress;
  /** A restored legacy block may say what it was, but may not launch anything. */
  isReadOnly: boolean;
  /**
   * Folds a finished set away, given by the block because the block owns the
   * folded state. "Done" is the footer's action only while there is something
   * to fold; without a handler it stays the label for the state that it was —
   * which is also how a set whose result row the thread already shows keeps its
   * old behaviour, rather than saying the same summary a second time.
   */
  onDone?: () => void;
  /**
   * The block's handle on this row's button, so unfolding can put the focus
   * back where the student left it.
   */
  doneRef?: RefObject<HTMLButtonElement>;
  /**
   * Whether the block drew an items area above the footer. It decides two
   * things: the rule (a lazy set has nothing above it but the block's own
   * header, and two rules with nothing between them is a seam) and whether the
   * spent-allowance line is worth repeating — the items area already says it.
   */
  hasItems?: boolean;
  /**
   * Asked before the launch request goes out. Resolving false abandons the
   * launch — the student chose to stay in the session they already had open —
   * and the footer goes back to rest with nothing said.
   */
  onBeforeLaunch?: () => Promise<boolean>;
  /** Where a launched set sends the student. */
  onLaunched: (
    target: ChatNavigationTarget,
    set: ChatStudySetBlockPayload
  ) => void;
}

const StudySetFooter = ({
  payload,
  progress,
  isReadOnly,
  hasItems = false,
  onDone,
  doneRef,
  onBeforeLaunch,
  onLaunched,
}: StudySetFooterProps) => {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const label = studySetButtonLabel(payload, progress);
  const isFinished = label === 'Done';
  /** Whether "Done" does something: it folds the set away (ORA-833). */
  const canFold = isFinished && !isReadOnly && Boolean(onDone);

  const limitReached =
    payload.kind === 'question' && payload.limits?.qbankRemaining === 0;

  const openFullSession = async () => {
    if (isPending) return;

    setIsPending(true);
    setError(null);

    try {
      if (onBeforeLaunch && !(await onBeforeLaunch())) return;

      const result = await launchStudySet(payload.setId);

      if (!result.launched) {
        setError(REFUSAL_COPY[result.reason]);
        return;
      }

      onLaunched(result.target, result.set);
    } catch {
      setError(LAUNCH_FAILED_COPY);
    } finally {
      setIsPending(false);
    }
  };

  /**
   * A set with no items and no full session behind it — a review set whose one
   * question has since been suspended (ORA-831) — has nothing for the student to
   * do, and says so instead of drawing a header over an empty row.
   */
  const isEmpty = !hasItems && label === null;

  const reason =
    error ??
    (isEmpty
      ? LAUNCH_UNAVAILABLE_COPY
      : limitReached && !hasItems
        ? QBANK_LIMIT_REACHED_COPY
        : null);

  return (
    <div
      className={cn(
        'flex min-h-11 flex-col justify-center gap-1 px-3 py-2 mobile:flex-row mobile:items-center mobile:justify-between mobile:gap-3',
        hasItems && 'border-t border-border-subtle'
      )}
      data-testid="study-set-footer"
    >
      <div className="min-w-0">
        {/* The block's own header already says what the set is (the registry
            title is `payload.scopeLabel`), so the footer only says how far
            through it the student is. */}
        <p className="m-0 text-xs text-fg-secondary">
          {studySetProgressText(progress)}
        </p>
        {reason ? (
          <p className="m-0 text-xs text-fg-secondary" role="status">
            {reason}
          </p>
        ) : null}
      </div>
      {label ? (
        <Button
          className="min-h-11 mobile:shrink-0"
          disabled={
            isFinished
              ? // Folding makes no request and spends no allowance, so a spent
                // one does not stand in its way; a read-only block folds
                // nothing, and "Done" is its label for a set that is over.
                !canFold
              : isReadOnly ||
                // Reviewing spends no allowance: the questions are answered and
                // the review screen only shows what they were.
                (limitReached && label !== 'Review') ||
                isPending
          }
          onClick={canFold ? onDone : () => void openFullSession()}
          ref={doneRef}
          size="sm"
          type="button"
        >
          {isPending ? <Spinner size={14} /> : null}
          {label}
        </Button>
      ) : null}
    </div>
  );
};

export default StudySetFooter;
